// LanguageCapability — text + voice capability story across 12 languages.
// Three feature cards (native text, voice cloning across languages, Lea's
// own voice in every language) and a grid of native-script language pills
// at the bottom to make the global reach feel real to non-English visitors.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";
import { TextAa, Microphone, GlobeHemisphereWest } from "@phosphor-icons/react";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const FEATURE_ICONS = [TextAa, Microphone, GlobeHemisphereWest];

export function LanguageCapability() {
    const { t } = useTranslation();
    const languages = t("language_capability.languages", { returnObjects: true });
    const langList = Array.isArray(languages) ? languages : [];
    const features = [
        { titleKey: "feature_text_title",    bodyKey: "feature_text_body",    Icon: FEATURE_ICONS[0] },
        { titleKey: "feature_voice_title",   bodyKey: "feature_voice_body",   Icon: FEATURE_ICONS[1] },
        { titleKey: "feature_founder_title", bodyKey: "feature_founder_body", Icon: FEATURE_ICONS[2] },
    ];
    return (
        <section
            className="relative py-32 px-6 bg-[#FDFBF7] overflow-hidden"
            data-testid="language-capability-section"
            id="languages"
        >
            <FmnWatermark position="bottom-right" size={740} opacity={0.05} rotate={12} />
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex justify-center mb-6">
                        <ForgetMeNotIcon size={44} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("language_capability.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("language_capability.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("language_capability.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-7 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("language_capability.lead")}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                    {features.map((f, i) => {
                        const Icon = f.Icon;
                        return (
                            <motion.div
                                key={f.titleKey}
                                {...FADE}
                                transition={{ ...FADE.transition, delay: 0.1 + i * 0.12 }}
                                className="rounded-[2rem] bg-white p-8 sm:p-10 border border-[#E5E1D8] shadow-rem-soft"
                                data-testid={`lang-feature-${i + 1}`}
                            >
                                <div className="w-12 h-12 rounded-2xl bg-[#F4F6FB] flex items-center justify-center mb-5">
                                    <Icon size={24} weight="duotone" color="#4A5A95" />
                                </div>
                                <h3 className="font-serif text-2xl text-[#2D2B2A] mb-3 leading-tight">
                                    {t(`language_capability.${f.titleKey}`)}
                                </h3>
                                <p className="text-[#5C5A59] leading-relaxed text-sm">
                                    {t(`language_capability.${f.bodyKey}`)}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.45 }}
                    className="mt-16 text-center"
                    data-testid="lang-pills"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-[#8A8786] font-semibold mb-6">
                        {t("language_capability.languages_label")}
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
                        {langList.map((l) => (
                            <span
                                key={l.code}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E1D8] text-sm text-[#2D2B2A]"
                                data-testid={`lang-pill-${l.code}`}
                            >
                                <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8786] font-semibold">
                                    {l.code}
                                </span>
                                <span className="font-serif">{l.native}</span>
                            </span>
                        ))}
                    </div>
                    <p className="mt-7 text-sm text-[#8A8786] max-w-xl mx-auto italic">
                        {t("language_capability.more_languages_note")}
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
